import type { ClientGame, ClientGameCtx } from "./index";
import { sound } from "../sound";
import { saveBubble } from "../bubbleSave";
import { countdownOverlay } from "./asteroids";
import { C, TINT, displayFont } from "../theme";
import { OUTLINE, cachedGrad, roundRectPath } from "../draw";
import { hudRowY, HUD_PILL_H, pillRowRight, boardPanel, boardOutline } from "./snake";

const PALETTE: Record<string, string> = {
  "1": C.coral, "2": C.mango, "3": C.green, "4": C.cobalt, "5": C.grape,
};
const FX_COLS = [C.white, TINT.mango, TINT.bubblegum, TINT.cobalt];
const WEAPONS: { k: string; label: string; icon: string }[] = [
  { k: "bomb", label: "BOMB", icon: "◈" },
  { k: "missile", label: "MISSILE", icon: "➤" },
  { k: "ballistic", label: "BALLISTIC", icon: "⤢" },
];

interface Fx { x: number; y: number; vx: number; vy: number; ttl: number; col: string }

export class BubbleClient implements ClientGame {
  private ctx!: ClientGameCtx;
  private canvas!: HTMLCanvasElement;
  private wrap!: HTMLDivElement;
  private weaponBtns: Record<string, HTMLButtonElement> = {};
  private raf = 0;
  private aiming = false;
  private fx: Fx[] = [];
  private view = { ox: 0, oy: 0, ts: 20 };
  private onKey = (e: KeyboardEvent) => this.key(e);

  private get bs() { return this.ctx.room.state.bubble; }

  mount(ctx: ClientGameCtx) {
    this.ctx = ctx;
    sound.unlock();
    this.wrap = document.createElement("div");
    this.wrap.className = "bb-wrap";
    this.canvas = document.createElement("canvas");
    this.canvas.id = "game-canvas";
    this.wrap.appendChild(this.canvas);

    const bar = document.createElement("div");
    bar.className = "bb-weapons";
    for (const w of WEAPONS) {
      const b = document.createElement("button");
      b.className = "bb-weapon";
      b.innerHTML = `<span class="wi">${w.icon}</span><span class="wl">${w.label}</span><span class="wc" data-c>0</span>`;
      b.onclick = () => {
        const armed = this.bs.armed === w.k;
        ctx.room.send("arm", { kind: armed ? "none" : w.k });
      };
      this.weaponBtns[w.k] = b;
      bar.appendChild(b);
    }
    this.wrap.appendChild(bar);

    const fire = document.createElement("div");
    fire.className = "punch-btn bb-fire";
    fire.textContent = "FIRE";
    const doFire = (e: Event) => { e.preventDefault(); ctx.room.send("fire"); };
    fire.addEventListener("touchstart", doFire, { passive: false });
    fire.addEventListener("mousedown", doFire);
    this.wrap.appendChild(fire);

    ctx.root.appendChild(this.wrap);

    const hint = document.createElement("div");
    hint.className = "touch-hint";
    hint.textContent = "drag to aim · tap FIRE to shoot";
    ctx.root.appendChild(hint);
    setTimeout(() => hint.remove(), 4200);

    this.canvas.addEventListener("touchstart", this.aimStart, { passive: false });
    this.canvas.addEventListener("touchmove", this.aimMove, { passive: false });
    this.canvas.addEventListener("touchend", this.aimEnd);
    this.canvas.addEventListener("mousedown", this.aimStart as any);
    this.canvas.addEventListener("mousemove", this.aimMove as any);
    this.canvas.addEventListener("mouseup", this.aimEnd as any);
    window.addEventListener("keydown", this.onKey);

    ctx.room.onMessage("b-fire", () => sound.laser());
    ctx.room.onMessage("b-stick", () => sound.chomp(Math.random() < 0.5));
    ctx.room.onMessage("b-pop", (m: any) => {
      sound.rockBoom(m?.kind === "match" ? 1 : 3);
      this.spawnFx(m?.at || []);
    });
    ctx.room.onMessage("b-drop", (m: any) => { sound.invaderHit(); this.spawnFx(m?.at || []); });
    ctx.room.onMessage("b-earn", () => sound.gulp());
    ctx.room.onMessage("b-level", () => sound.levelUp());
    ctx.room.onMessage("b-over", () => sound.arcadeDeath());
    ctx.room.onMessage("b-save", (m: any) => saveBubble(m));

    this.loop();
  }

  unmount() {
    cancelAnimationFrame(this.raf);
    window.removeEventListener("keydown", this.onKey);
  }

  private pointAim(clientX: number, clientY: number) {
    const rect = this.canvas.getBoundingClientRect();
    const { ox, oy, ts } = this.view;
    const lx = ox + (this.bs.cols / 2) * ts;
    const ly = oy + (this.bs.rows + 0.4) * ts;
    const dx = clientX - rect.left - lx;
    const dy = clientY - rect.top - ly;
    let deg = (Math.atan2(-dy, dx) * 180) / Math.PI;
    deg = Math.max(12, Math.min(168, deg));
    this.ctx.room.send("aim", { deg });
  }
  private aimStart = (e: any) => {
    e.preventDefault();
    this.aiming = true;
    const t = e.touches?.[0] ?? e;
    this.pointAim(t.clientX, t.clientY);
  };
  private aimMove = (e: any) => {
    if (!this.aiming) return;
    e.preventDefault();
    const t = e.touches?.[0] ?? e;
    this.pointAim(t.clientX, t.clientY);
  };
  private aimEnd = (_e: any) => {
    // drag only aims — fire with the FIRE button (or space)
    this.aiming = false;
  };
  private key(e: KeyboardEvent) {
    if (e.code === "ArrowLeft") this.ctx.room.send("aim", { deg: Math.min(168, this.bs.aim + 4) });
    else if (e.code === "ArrowRight") this.ctx.room.send("aim", { deg: Math.max(12, this.bs.aim - 4) });
    else if (e.code === "Space") this.ctx.room.send("fire");
    else if (e.code === "Digit1") this.ctx.room.send("arm", { kind: "bomb" });
    else if (e.code === "Digit2") this.ctx.room.send("arm", { kind: "missile" });
    else if (e.code === "Digit3") this.ctx.room.send("arm", { kind: "ballistic" });
    else return;
    e.preventDefault();
  }

  private spawnFx(idx: number[]) {
    const cols = this.bs.cols;
    for (const p of idx.slice(0, 40)) {
      const r = Math.floor(p / cols), c = p % cols;
      for (let i = 0; i < 3; i++) {
        const a = Math.random() * Math.PI * 2;
        this.fx.push({
          x: c + 0.5, y: r + 0.5,
          vx: Math.cos(a) * 0.004, vy: Math.sin(a) * 0.004,
          ttl: 380, col: FX_COLS[(p + i) % FX_COLS.length]!,
        });
      }
    }
  }

  private loop = () => {
    try { this.draw(); } catch (e) { console.error("bubble frame", e); }
    this.raf = requestAnimationFrame(this.loop);
  };

  private draw() {
    const bs = this.bs;
    const c = this.canvas;
    const g = c.getContext("2d")!;
    const dpr = Math.min(2, devicePixelRatio || 1);
    const cw = c.clientWidth, ch = c.clientHeight;
    if (cw < 40 || ch < 40) return;
    if (c.width !== cw * dpr || c.height !== ch * dpr) { c.width = cw * dpr; c.height = ch * dpr; }
    g.save();
    g.scale(dpr, dpr);
    // sky scene, edge to edge
    g.fillStyle = cachedGrad(`bb-bg-${ch}`, () => {
      const gr = g.createLinearGradient(0, 0, 0, ch);
      gr.addColorStop(0, TINT.cobalt);
      gr.addColorStop(1, TINT.bubblegum);
      return gr;
    });
    g.fillRect(0, 0, cw, ch);

    const cols = bs.cols || 11, rows = bs.rows || 14;
    // bubble runs are always solo, so the HUD row sits below the best banner
    const hudY = hudRowY(true);
    const oy = hudY + HUD_PILL_H + 16;
    // keep the bottom ~16% for the weapon column / FIRE button
    const ts = Math.max(8, Math.min((cw - 24) / cols, (ch * 0.84 - oy) / (rows + 2)));
    const ox = (cw - cols * ts) / 2;
    this.view = { ox, oy, ts };
    const px = (gx: number) => ox + gx * ts;
    const py = (gy: number) => oy + gy * ts;

    // field — a white sticker board
    const fr = Math.max(8, ts * 0.45);
    boardPanel(g, ox - 4, oy - 4, cols * ts + 8, rows * ts + 8, fr, "rgba(255,255,255,0.88)");
    // danger line
    g.strokeStyle = C.coral;
    g.lineWidth = 2;
    g.setLineDash([6, 6]);
    g.beginPath();
    g.moveTo(ox, py(rows - 1));
    g.lineTo(ox + cols * ts, py(rows - 1));
    g.stroke();
    g.setLineDash([]);

    // bubbles
    const cells = bs.cells || "";
    for (let r = 0; r < rows; r++) {
      for (let col = 0; col < cols; col++) {
        const ch2 = cells[r * cols + col];
        if (!ch2 || ch2 === ".") continue;
        bubble(g, px(col + 0.5), py(r + 0.5), ts * 0.46, PALETTE[ch2] || C.faint);
      }
    }
    boardOutline(g, ox - 4, oy - 4, cols * ts + 8, rows * ts + 8, fr);

    // launcher + aim guide
    const lx = px(cols / 2), ly = py(rows + 0.4);
    const rad = (bs.aim * Math.PI) / 180;
    g.strokeStyle = "rgba(27,21,52,0.45)";
    g.lineWidth = 2.5;
    g.lineCap = "round";
    g.setLineDash([2, 7]);
    g.beginPath();
    g.moveTo(lx, ly);
    g.lineTo(lx + Math.cos(rad) * ts * 6, ly - Math.sin(rad) * ts * 6);
    g.stroke();
    g.setLineDash([]);
    // launcher base — a white sticker plate under the ball
    roundRectPath(g, lx - ts * 0.75 + 2, ly - ts * 0.1 + 4, ts * 1.5, ts * 0.75, ts * 0.3);
    g.fillStyle = C.ink; g.fill();
    roundRectPath(g, lx - ts * 0.75, ly - ts * 0.1, ts * 1.5, ts * 0.75, ts * 0.3);
    g.fillStyle = C.white; g.fill();
    g.strokeStyle = C.ink; g.lineWidth = 2.5; g.stroke();
    // current + next
    const curCol = PALETTE[bs.armed ? "3" : bs.cur] || C.faint;
    bubble(g, lx, ly, ts * 0.44, curCol);
    if (bs.armed) {
      // weapon initial on top of the loaded ball
      const fs = Math.round(ts * 0.5);
      g.font = displayFont(fs);
      g.textAlign = "center"; g.textBaseline = "middle";
      g.lineWidth = 3; g.lineJoin = "round"; g.strokeStyle = C.ink;
      g.strokeText(bs.armed[0]!.toUpperCase(), lx, ly + fs * 0.08);
      g.fillStyle = C.white;
      g.fillText(bs.armed[0]!.toUpperCase(), lx, ly + fs * 0.08);
    }
    bubble(g, lx + ts * 1.4, ly + ts * 0.2, ts * 0.3, PALETTE[bs.next] || C.faint);

    // shot
    if (bs.shotActive) {
      const col = bs.shotKind === "bomb" ? C.mango
        : bs.shotKind === "missile" ? C.cobalt
        : bs.shotKind === "ballistic" ? C.coral
        : PALETTE[bs.cur] || C.white;
      bubble(g, px(bs.shotX), py(bs.shotY), ts * 0.42, col);
    }

    // fx
    for (let i = this.fx.length - 1; i >= 0; i--) {
      const f = this.fx[i]!;
      f.ttl -= 16;
      if (f.ttl <= 0) { this.fx.splice(i, 1); continue; }
      f.x += f.vx * 16; f.y += f.vy * 16;
      g.globalAlpha = Math.max(0, f.ttl / 380);
      g.fillStyle = f.col;
      g.fillRect(px(f.x) - 2.5, py(f.y) - 2.5, 5, 5);
      g.strokeStyle = C.ink;
      g.lineWidth = 1.2;
      g.strokeRect(px(f.x) - 2.5, py(f.y) - 2.5, 5, 5);
      g.globalAlpha = 1;
    }

    // HUD — white sticker pills, top right below the best banner
    pillRowRight(g, cw - 12, hudY, [
      { text: String(bs.score ?? 0), label: "score" },
      { text: String(bs.level ?? 1), label: "level" },
      { text: String(bs.shotsToPush ?? 0), label: "drop in" },
    ]);

    // weapon button counts
    for (const w of WEAPONS) {
      const b = this.weaponBtns[w.k]!;
      const n = w.k === "bomb" ? bs.wBomb : w.k === "missile" ? bs.wMissile : bs.wBallistic;
      const cnt = b.querySelector<HTMLElement>("[data-c]")!;
      if (cnt.textContent !== String(n)) cnt.textContent = String(n);
      b.classList.toggle("armed", bs.armed === w.k);
      b.disabled = n <= 0 && bs.armed !== w.k;
    }

    if (this.ctx.room.state.phase === "countdown") {
      countdownOverlay(g, cw, ch, this.ctx.room.state.countdown);
    }
    g.restore();
  }

  results(ctx: ClientGameCtx): HTMLElement {
    const bs = ctx.room.state.bubble;
    saveBubble({
      level: bs.level, score: bs.score,
      wBomb: bs.wBomb, wMissile: bs.wMissile, wBallistic: bs.wBallistic,
    });
    const d = document.createElement("div");
    d.className = "arc-final";
    d.innerHTML =
      `<div class="score-big">${bs.score ?? 0}</div>` +
      `<div class="arc-note">reached level ${bs.level} &middot; progress saved</div>`;
    return d;
  }
}

/** a glossy bubble with an ink outline */
function bubble(g: CanvasRenderingContext2D, x: number, y: number, r: number, col: string) {
  g.beginPath();
  g.arc(x, y, r, 0, Math.PI * 2);
  g.fillStyle = col;
  g.fill();
  g.beginPath();
  g.arc(x - r * 0.3, y - r * 0.3, r * 0.3, 0, Math.PI * 2);
  g.fillStyle = "rgba(255,255,255,0.5)";
  g.fill();
  g.beginPath();
  g.arc(x, y, r - 0.6, 0, Math.PI * 2);
  g.strokeStyle = OUTLINE;
  g.lineWidth = Math.max(1.5, r * 0.13);
  g.stroke();
}
